import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Play, Headphones, Languages, Mic, Download, Sparkles, BookOpen, Users, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const slideImages = [
    'https://customer-assets.emergentagent.com/job_visual-story-forge/artifacts/3vxzd12o_Screenshot%202026-04-01%20at%209.34.14%E2%80%AFAM.png',
    'https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fd9050b35bdc7d5873beea_Screenshot_2023-09-10-10-44-04-921_com.legendhq.mobile.android.legendapp.jpg',
    'https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fd9063ee2e4966d6d0daa6_Screenshot_2023-09-10-10-44-45-224_com.legendhq.mobile.android.legendapp.jpg'
  ];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [slideImages.length]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in-up, .fade-in-left, .fade-in-right');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Headphones className="w-8 h-8" />,
      title: "Immersive Audio",
      description: "Full character voice acting brings every panel to life with cinema-quality sound design."
    },
    {
      icon: <Languages className="w-8 h-8" />,
      title: "Multi-Language",
      description: "Experience comics in your preferred language with seamless audio synchronization."
    },
    {
      icon: <Mic className="w-8 h-8" />,
      title: "Voice Recording",
      description: "Become the character by recording your own voice and sharing with the community."
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "All Comic Types",
      description: "Manga, Webtoons, Western comics, and graphic novels all in one platform."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Tap or Auto-Play",
      description: "Control your reading pace or sit back and enjoy an automated experience."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Exclusive Drops",
      description: "Get early access to exclusive titles from top publishers and creators."
    }
  ];

  const publishers = [
    { name: "Boom Studios", url: "https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64229b0b6b810b3284a7131e_boom-studios-small.png" },
    { name: "PSG", url: "https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64c4340c019c6926108332cb_psg_logo_aplat_rvb%20(1).png" },
    { name: "Image Comics", url: "https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/6357a366954c9c530c43fe83_Image-Comics-Logo.png" },
    { name: "Saturday AM", url: "https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fd8dbbb35bdc7d587195f0_saturdayam%20LOGO%20cleaned%20ToBeVectored%20(2).png" },
    { name: "Le Lombard", url: "https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/6357a366954c9c7bd343fe89_Le_Lombard_logo_(2010).svg.png" }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-hidden">
      <SEO 
        title="Legend - Audiovisual Digital Comics with Full Character Audio"
        description="Experience comics like never before with Legend's immersive audiovisual platform. Full character voice acting, multi-language support, and exclusive drops from top publishers including Boom Studios, PSG, Image Comics, and more. Download now on iOS and Android."
        keywords="audiovisual comics, digital comics, manga, webtoons, graphic novels, character audio, voice acting, interactive comics, Legend app, comic reader, audio comics, immersive storytelling"
        url="https://visual-story-forge.emergent.host/"
      />
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-[#FFB966]/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-1/2 -right-20 w-96 h-96 bg-[#FF8C42]/5 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-[#FFB966]/3 rounded-full blur-3xl animate-float"></div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/60 backdrop-blur-xl border-b border-[#FFB966]/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] blur-md opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex gap-0.5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="w-1.5 h-10 bg-gradient-to-b from-[#FFB966] to-[#FF8C42] rounded-full transform rotate-12" style={{ marginLeft: i > 0 ? '2px' : '0' }}></div>
                  ))}
                </div>
              </div>
              <span className="text-2xl font-bold tracking-wider bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">LEGEND</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium hover:text-[#FFB966] transition-colors relative group">
                Features
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] group-hover:w-full transition-all duration-300"></span>
              </a>
              <Link to="/partners" className="text-sm font-medium hover:text-[#FFB966] transition-colors relative group">
                Partners
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/about" className="text-sm font-medium hover:text-[#FFB966] transition-colors relative group">
                About Us
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/contact" className="text-sm font-medium hover:text-[#FFB966] transition-colors relative group">
                Contact
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Button onClick={() => setShowDownloadModal(true)} className="relative group bg-gradient-to-r from-[#FFB966] to-[#FF8C42] text-black font-semibold px-6 overflow-hidden">
                <span className="relative z-10 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Get Started
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFC57D] to-[#FFB966] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 fade-in-left relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB966]/10 border border-[#FFB966]/20 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-[#FFB966]" />
                <span className="text-sm font-medium text-[#FFB966]">Next-Gen Digital Comics</span>
              </div>
              
              <h1 className="text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight">
                Comics That
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-r from-[#FFB966] via-[#FFC57D] to-[#FFB966] bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">Come Alive</span>
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#FFB966]/20 to-[#FF8C42]/20 blur-2xl -z-10"></div>
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                Immerse yourself in audiovisual digital comics with full character audio, interactive features, and exclusive drops from your favorite storytellers.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://apps.apple.com/us/app/legendhq-audiovisual-comics/id6444578827" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fdb56d5a16efb4befdd9bb_Download%20on%20App%20Store.png" 
                    alt="Download on App Store" 
                    className="h-14 hover:scale-105 transition-transform cursor-pointer"
                  />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.legendhq.mobile.android.legendapp" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fdb5623ba4d520ddbde2aa_Download%20on%20Google%20Play.png" 
                    alt="Get it on Google Play" 
                    className="h-14 hover:scale-105 transition-transform cursor-pointer"
                  />
                </a>
              </div>
            </div>
            
            {/* Right Content - Phone Mockup with Slideshow */}
            <div className="relative fade-in-right">
              <div className="relative mx-auto w-fit">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFB966]/30 to-[#FF8C42]/30 blur-3xl animate-pulse-slow"></div>
                
                {/* Phone Frame with Slideshow */}
                <div className="relative">
                  <div className="relative w-full max-w-md mx-auto">
                    {slideImages.map((img, idx) => (
                      <div key={idx} className={`absolute top-0 left-0 w-full transition-opacity duration-1000 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="relative">
                          <div className="absolute -inset-2 bg-gradient-to-r from-[#FFB966]/20 to-[#FF8C42]/20 blur-xl rounded-3xl"></div>
                          <img 
                            src={img} 
                            alt={`Legend App Screenshot ${idx + 1}`} 
                            className="relative w-full h-auto rounded-3xl drop-shadow-2xl"
                            style={{ transform: `translateY(${scrollY * 0.1}px)`, aspectRatio: '9/19.5' }}
                          />
                        </div>
                      </div>
                    ))}
                    <img 
                      src={slideImages[0]} 
                      alt="Legend App" 
                      className="w-full rounded-3xl opacity-0"
                      style={{ aspectRatio: '9/19.5' }}
                    />
                  </div>
                  
                  {/* Play Button Overlay */}
                  <button
                    onClick={() => setShowVideoModal(true)}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl z-10 group"
                  >
                    <Play className="w-10 h-10 text-black fill-black ml-1 group-hover:scale-110 transition-transform" />
                  </button>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-[#FFB966] to-[#FF8C42] rounded-2xl flex items-center justify-center animate-float shadow-lg shadow-[#FFB966]/50">
                  <Headphones className="w-10 h-10 text-black" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { number: "200+", label: "Titles" },
              { number: "5", label: "Languages" },
              { number: "4.5", label: "App Rating" }
            ].map((stat, idx) => (
              <div key={idx} className="text-center fade-in-up" style={{ animationDelay: `${idx * 100}ms` }}>
                <div className="text-5xl font-bold bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publishers Section */}
      <section id="publishers" className="py-20 px-6 bg-gradient-to-b from-transparent via-black/40 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Partnered with the <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Best</span>
            </h2>
            <p className="text-xl text-gray-400">Exclusive drops from top storytellers and publishers worldwide</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center">
            {publishers.map((publisher, idx) => (
              <div key={idx} className="fade-in-up group" style={{ animationDelay: `${idx * 100}ms` }}>
                <div className="relative p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 border border-white/5 hover:border-[#FFB966]/30 transition-all duration-300 hover:shadow-lg hover:shadow-[#FFB966]/10">
                  <img 
                    src={publisher.url} 
                    alt={publisher.name} 
                    className="w-full h-16 object-contain opacity-60 group-hover:opacity-100 transition-opacity filter grayscale group-hover:grayscale-0"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Legend</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">The only platform that delivers comics with vibrant audio tracks that bring stories to life</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="fade-in-up group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#FFB966]/10 hover:border-[#FFB966]/30 transition-all duration-500 overflow-hidden" style={{ animationDelay: `${idx * 100}ms` }}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFB966]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <CardContent className="p-8 relative z-10">
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-[#FFB966]/20 to-[#FF8C42]/20 flex items-center justify-center text-[#FFB966] group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-[#FFB966] transition-colors">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-6 bg-gradient-to-b from-transparent via-black/40 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-left space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB966]/10 border border-[#FFB966]/20">
                <Sparkles className="w-4 h-4 text-[#FFB966]" />
                <span className="text-sm font-medium text-[#FFB966]">Full Character Audio</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold">
                Hear Every
                <br />
                <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Character Speak</span>
              </h2>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Legend enhances digital comics with high-quality audio narration and character scripts in multiple languages. You can even record yourself as the character in your favorite comics.
              </p>
              
              <div className="space-y-4 pt-4">
                {[
                  { icon: <Headphones className="w-5 h-5" />, text: "Professional voice actors" },
                  { icon: <Languages className="w-5 h-5" />, text: "Multiple language options" },
                  { icon: <Mic className="w-5 h-5" />, text: "Record your own voice" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FFB966]/20 to-[#FF8C42]/20 flex items-center justify-center text-[#FFB966]">
                      {item.icon}
                    </div>
                    <span className="text-gray-300">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="fade-in-right relative">
              <div className="relative rounded-3xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFB966]/20 to-[#FF8C42]/20 blur-2xl"></div>
                <img 
                  src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/6357a366954c9c906c43fede_Audio.png" 
                  alt="Audio Feature" 
                  className="relative w-full rounded-3xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Varieties Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-left relative order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFB966]/20 to-[#FF8C42]/20 blur-2xl"></div>
                <img 
                  src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/67d331d7b8165b375820fc95_Final%20replacement.png" 
                  alt="Comic Varieties" 
                  className="relative w-full rounded-3xl shadow-2xl"
                />
              </div>
            </div>
            
            <div className="fade-in-right space-y-6 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB966]/10 border border-[#FFB966]/20">
                <BookOpen className="w-4 h-4 text-[#FFB966]" />
                <span className="text-sm font-medium text-[#FFB966]">All Comic Types</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold">
                Every Style,
                <br />
                <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">One Platform</span>
              </h2>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                At Legend we do regular drops of unique and exclusive titles. Our reader uniquely serves all varieties of comics including Manga, Webtoons, and Western-style comics.
              </p>
              
              <div className="grid grid-cols-2 gap-4 pt-4">
                {["Manga", "Webtoons", "Graphic Novels", "Western Comics"].map((type, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#FFB966]/10 hover:border-[#FFB966]/30 transition-all">
                    <span className="text-[#FFB966] font-semibold">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#FFB966]/20 p-12 md:p-16 text-center fade-in-up">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB966]/10 via-transparent to-[#FF8C42]/10"></div>
            
            <div className="relative z-10 space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold">
                Ready to Experience Comics Like <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Never Before?</span>
              </h2>
              
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Download the Legend app today and dive into a world of audiovisual storytelling.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                <a href="https://apps.apple.com/us/app/legendhq-audiovisual-comics/id6444578827" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fdb56d5a16efb4befdd9bb_Download%20on%20App%20Store.png" 
                    alt="Download on App Store" 
                    className="h-14 hover:scale-105 transition-transform cursor-pointer"
                  />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.legendhq.mobile.android.legendapp" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fdb5623ba4d520ddbde2aa_Download%20on%20Google%20Play.png" 
                    alt="Get it on Google Play" 
                    className="h-14 hover:scale-105 transition-transform cursor-pointer"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="w-1.5 h-10 bg-gradient-to-b from-[#FFB966] to-[#FF8C42] rounded-full transform rotate-12" style={{ marginLeft: i > 0 ? '2px' : '0' }}></div>
                ))}
              </div>
              <span className="text-xl font-bold tracking-wider bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">LEGEND</span>
            </div>
            
            <div className="text-gray-500 text-sm">
              © 2026 Zing Technologies Inc. All rights reserved.
            </div>
            
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-400 hover:text-[#FFB966] transition-colors">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-[#FFB966] transition-colors">Terms</Link>
              <Link to="/contact" className="text-gray-400 hover:text-[#FFB966] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="max-w-4xl bg-[#0a0a0a] border-[#FFB966]/20">
          <DialogHeader>
            <DialogTitle className="text-white">Legend Demo</DialogTitle>
          </DialogHeader>
          <div className="aspect-video">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/AIlWDAeCtQA"
              title="Legend Demo Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-lg"
            ></iframe>
          </div>
        </DialogContent>
      </Dialog>

      {/* Download Modal */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent className="max-w-md bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#FFB966]/20">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl text-center mb-4">Download Legend</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-300 text-center">Choose your platform to get started</p>
            <div className="flex flex-col gap-4">
              <a href="https://apps.apple.com/us/app/legendhq-audiovisual-comics/id6444578827" target="_blank" rel="noopener noreferrer" className="flex justify-center">
                <img 
                  src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fdb56d5a16efb4befdd9bb_Download%20on%20App%20Store.png" 
                  alt="Download on App Store" 
                  className="h-14 hover:scale-105 transition-transform cursor-pointer"
                />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.legendhq.mobile.android.legendapp" target="_blank" rel="noopener noreferrer" className="flex justify-center">
                <img 
                  src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fdb5623ba4d520ddbde2aa_Download%20on%20Google%20Play.png" 
                  alt="Get it on Google Play" 
                  className="h-14 hover:scale-105 transition-transform cursor-pointer"
                />
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
